import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toPaise } from '@vns/core';
import { supabase } from './supabase';
import { useSession } from './session';

/**
 * Company money: bank accounts, transfers between them, and the stock-transfer
 * register.
 *
 * Bank accounts and fund transfers are admin-only at the database level (0010),
 * so these hooks return nothing at all for site staff rather than an empty list
 * they might mistake for "no data".
 */

function unwrap<T>({ data, error }: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (error) throw new Error(error.message);
  if (data === null || data === undefined) throw new Error('No data returned');
  return data as NonNullable<T>;
}

/* ---------------------------- bank accounts ---------------------------- */

export interface BankAccountRow {
  id: string;
  bankName: string;
  accountHolder: string;
  branch: string | null;
  accountNo: string | null;
  ifsc: string | null;
  gpayPhone: string | null;
  isCash: boolean;
  /** Opening balance adjusted by every completed transfer in and out. */
  balancePaise: number;
}

export function useBankAccounts() {
  return useQuery({
    queryKey: ['bank-accounts'],
    queryFn: async (): Promise<BankAccountRow[]> => {
      const accounts = unwrap(
        await supabase
          .from('bank_accounts')
          .select('id, bank_name, account_holder, branch, account_no, ifsc, gpay_phone, is_cash, opening_balance_paise')
          .is('deleted_at', null)
          .order('is_cash')
          .order('bank_name'),
      );

      // Balance is derived from transfers rather than stored, so it can never
      // drift out of step with the transfer register.
      const transfers = unwrap(
        await supabase
          .from('fund_transfers')
          .select('from_account_id, to_account_id, amount_paise')
          .eq('approval_status', 'approved')
          .is('deleted_at', null),
      );

      const delta = new Map<string, number>();
      for (const t of transfers) {
        delta.set(t.from_account_id, (delta.get(t.from_account_id) ?? 0) - t.amount_paise);
        delta.set(t.to_account_id, (delta.get(t.to_account_id) ?? 0) + t.amount_paise);
      }

      return accounts.map((a) => ({
        id: a.id,
        bankName: a.bank_name,
        accountHolder: a.account_holder,
        branch: a.branch,
        accountNo: a.account_no,
        ifsc: a.ifsc,
        gpayPhone: a.gpay_phone,
        isCash: a.is_cash,
        balancePaise: a.opening_balance_paise + (delta.get(a.id) ?? 0),
      }));
    },
  });
}

export interface NewBankAccount {
  bank_name: string;
  account_holder: string;
  branch?: string;
  account_no?: string;
  ifsc?: string;
  gpay_phone?: string;
  is_cash: boolean;
  openingRupees?: string;
}

export function useCreateBankAccount() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);
  return useMutation({
    mutationFn: async (v: NewBankAccount) => {
      if (!actor) throw new Error('Not signed in');
      return unwrap(
        await supabase.from('bank_accounts').insert({
          company_id: actor.companyId,
          bank_name: v.bank_name.trim(),
          account_holder: v.account_holder.trim(),
          branch: v.branch?.trim() || null,
          account_no: v.account_no?.trim() || null,
          ifsc: v.ifsc?.trim().toUpperCase() || null,
          gpay_phone: v.gpay_phone?.trim() || null,
          is_cash: v.is_cash,
          opening_balance_paise: v.openingRupees?.trim() ? toPaise(v.openingRupees) : 0,
          created_by: actor.id,
        }).select('id').single(),
      );
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bank-accounts'] }),
  });
}

/* ---------------------------- fund transfers ---------------------------- */

export interface FundTransferRow {
  id: string;
  date: string;
  fromName: string;
  toName: string;
  amountPaise: number;
  mode: string;
  referenceNo: string | null;
  utrNo: string | null;
  remarks: string | null;
}

export function useFundTransfers() {
  return useQuery({
    queryKey: ['fund-transfers'],
    queryFn: async (): Promise<FundTransferRow[]> => {
      const rows = unwrap(
        await supabase
          .from('fund_transfers')
          .select('id, transferred_on, amount_paise, mode, reference_no, utr_no, remarks, from_account:bank_accounts!fund_transfers_from_account_id_fkey(bank_name), to_account:bank_accounts!fund_transfers_to_account_id_fkey(bank_name)')
          .is('deleted_at', null)
          .order('transferred_on', { ascending: false }),
      );
      const name = (v: unknown) => (v as { bank_name: string } | null)?.bank_name ?? '—';
      return rows.map((r) => ({
        id: r.id,
        date: r.transferred_on,
        fromName: name(r.from_account),
        toName: name(r.to_account),
        amountPaise: r.amount_paise,
        mode: r.mode,
        referenceNo: r.reference_no,
        utrNo: r.utr_no,
        remarks: r.remarks,
      }));
    },
  });
}

export interface NewFundTransfer {
  from_account_id: string;
  to_account_id: string;
  date: string;
  amountRupees: string;
  mode: string;
  reference_no?: string;
  utr_no?: string;
  remarks?: string;
}

export function useCreateFundTransfer() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);
  return useMutation({
    mutationFn: async (v: NewFundTransfer) => {
      if (!actor) throw new Error('Not signed in');
      if (v.from_account_id === v.to_account_id) {
        throw new Error('Choose two different accounts');
      }
      return unwrap(
        await supabase.from('fund_transfers').insert({
          company_id: actor.companyId,
          from_account_id: v.from_account_id,
          to_account_id: v.to_account_id,
          transferred_on: v.date,
          amount_paise: toPaise(v.amountRupees),
          mode: v.mode as never,
          reference_no: v.reference_no?.trim() || null,
          utr_no: v.utr_no?.trim() || null,
          remarks: v.remarks?.trim() || null,
          approval_status: 'approved',
        }).select('id').single(),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fund-transfers'] });
      void qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}

/* --------------------------- stock transfers --------------------------- */

export interface StockTransferRow {
  id: string;
  date: string;
  materialId: string;
  materialName: string;
  fromProjectId: string;
  fromName: string;
  toProjectId: string;
  toName: string;
  quantity: number;
  unitId: string | null;
  unit: string;
  ratePaise: number | null;
  amountPaise: number | null;
  vehicleNo: string | null;
  driverName: string | null;
  remarks: string | null;
  status: string;
  approvalStatus: string;
  /** A line that belongs to a bulk trip — its header carries the vehicle,
   *  driver and approval decision, so it is still editable but not the thing
   *  that decides its own approval. */
  batchId: string | null;
}

export interface StockTransferFilters {
  /** Either side counts — a site that received material moved as much as one that sent it. */
  projectId?: string;
  materialId?: string;
}

/** The company-wide register. A transfer can also be created here directly,
 *  or from inside a project via Site Transfer — both write the same row. */
export function useStockTransfers(status: string, filters: StockTransferFilters = {}) {
  return useQuery({
    queryKey: ['stock-transfers', status, filters.projectId ?? '', filters.materialId ?? ''],
    queryFn: async (): Promise<StockTransferRow[]> => {
      let q = supabase
        .from('stock_transfers')
        .select('id, transferred_on, material_id, quantity, unit_id, rate_paise, amount_paise, vehicle_no, driver_name, remarks, status, approval_status, batch_id, from_project_id, to_project_id, materials(name), units(short_name), from_project:projects!stock_transfers_from_project_id_fkey(name), to_project:projects!stock_transfers_to_project_id_fkey(name)')
        .is('deleted_at', null)
        .order('transferred_on', { ascending: false });
      if (status) q = q.eq('approval_status', status);
      if (filters.projectId) {
        q = q.or(`from_project_id.eq.${filters.projectId},to_project_id.eq.${filters.projectId}`);
      }
      if (filters.materialId) q = q.eq('material_id', filters.materialId);

      const rows = unwrap(await q);
      const rel = (v: unknown, k = 'name') => (v as Record<string, string> | null)?.[k] ?? '—';
      return rows.map((r) => ({
        id: r.id,
        date: r.transferred_on,
        materialId: r.material_id,
        materialName: rel(r.materials),
        fromProjectId: r.from_project_id,
        fromName: rel(r.from_project),
        toProjectId: r.to_project_id,
        toName: rel(r.to_project),
        quantity: Number(r.quantity),
        unitId: r.unit_id,
        unit: rel(r.units, 'short_name'),
        ratePaise: r.rate_paise != null ? Number(r.rate_paise) : null,
        amountPaise: r.amount_paise != null ? Number(r.amount_paise) : null,
        vehicleNo: r.vehicle_no,
        driverName: r.driver_name,
        remarks: r.remarks,
        status: r.status,
        approvalStatus: r.approval_status,
        batchId: r.batch_id,
      }));
    },
  });
}

// The company-wide register's own "Add Transfer" used to post a single line
// here (useCreateStockTransfer). It now goes through
// useCreateTransferBatch in lib/stock-transfer-batches.ts instead, so several
// materials on one trip can be recorded at once — see 0016.

/**
 * Correcting or removing one line from the register directly — not tied to a
 * fixed project the way `useUpdateEntry`/`useDeleteEntry` in project-edit.ts
 * are, since neither site is implicit here. This edits the one row, whether
 * it stands on its own or is part of a bulk trip; the trip's header and its
 * other materials are left untouched, the same way correcting one line of a
 * supplier bill from a project's own ledger never touches the bill itself.
 */
export function useUpdateStockTransfer() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (v: {
      id: string; date: string; from_project_id: string; to_project_id: string;
      material_id: string; unit_id?: string; quantity: string;
      rateRupees?: string;
      vehicle_no?: string; driver_name?: string; remarks?: string;
    }) => {
      if (v.to_project_id === v.from_project_id) throw new Error('Choose two different sites');
      const qty = Number(v.quantity);
      const ratePaise = v.rateRupees && v.rateRupees.trim() ? toPaise(v.rateRupees) : null;
      const amountPaise = ratePaise != null ? Math.round(qty * ratePaise) : null;

      const { error } = await supabase.from('stock_transfers').update({
        transferred_on: v.date,
        from_project_id: v.from_project_id, to_project_id: v.to_project_id,
        material_id: v.material_id, unit_id: v.unit_id || null,
        quantity: qty,
        rate_paise: ratePaise,
        amount_paise: amountPaise,
        vehicle_no: v.vehicle_no?.trim() || null,
        driver_name: v.driver_name?.trim() || null,
        remarks: v.remarks?.trim() || null,
      }).eq('id', v.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, v) => {
      void qc.invalidateQueries({ queryKey: ['stock-transfers'] });
      for (const projectId of [v.from_project_id, v.to_project_id]) {
        void qc.invalidateQueries({ queryKey: ['project', projectId] });
        void qc.invalidateQueries({ queryKey: ['project-stock-map', projectId] });
      }
      void qc.invalidateQueries({ queryKey: ['project-stock-map'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['materials'] });
      void qc.invalidateQueries({ queryKey: ['material'] });
      void qc.invalidateQueries({ queryKey: ['material-full-report'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['my-submissions'] });
    },
  });
}

export function useDeleteStockTransfer() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('stock_transfers')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock-transfers'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['project-stock-map'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['materials'] });
      void qc.invalidateQueries({ queryKey: ['material'] });
      void qc.invalidateQueries({ queryKey: ['material-full-report'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['my-submissions'] });
    },
  });
}

export function useRestoreStockTransfer() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('stock_transfers')
        .update({ deleted_at: null })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock-transfers'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['project-stock-map'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['materials'] });
      void qc.invalidateQueries({ queryKey: ['material'] });
      void qc.invalidateQueries({ queryKey: ['material-full-report'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['my-submissions'] });
    },
  });
}
